import type { FC } from 'react';
import { memo, useMemo } from 'react';
// import { useJackpotsStore } from '../../store/jackpotsStore';
import { useJackpotsStore } from '../../store/jackpotsStoreSimple';
import { getJackpotStatisticsLevelKeys } from '../../statisticsAssets';
import {
  MysteryJackpot,
  HighCashJackpot,
  BellLinkJackpot,
  BellLinkBoostJackpot,
  GodsAndKingsJackpot,
  VipBellLinkJackpot,
  VipBellLinkBoostJackpot,
  VipGodsAndKingsJackpot,
  InstantLinkJackpot,
  PremiumLinkJackpot,
  StandardJackpot,
  StaticJackpot
} from '../variants';

import type { JackpotComponentMap, JackpotPanelProps } from '../../types/jackpot.types';
import { EGT } from '@repo/lobby2.0/src/constants';
import { DEFAULT_DENOMINATION } from '../../constants'
import { getGamesMinDenomination } from '@repo/common';

import styles from './JackpotPanel.module.css';
import { useCurrency } from '../../../../store/app/appStore';
import { testIds } from '../../../../core/testIds';

const componentJackpotMap: JackpotComponentMap = {
  // Exact matches from console logs
  'Bell Link': BellLinkJackpot,
  'Bell Link Boost': BellLinkBoostJackpot,
  'High Cash': HighCashJackpot,
  'InstantLink': InstantLinkJackpot,
  'Clover Chance': MysteryJackpot,
  'VIP Bell Link': VipBellLinkJackpot,
  'VIP Bell Link Boost': VipBellLinkBoostJackpot,
  'VIP High Cash': HighCashJackpot,
  'progressive': StaticJackpot,
  'Gods and Kings': GodsAndKingsJackpot,
  'VIP Gods and Kings': VipGodsAndKingsJackpot,
  'Premium Link': PremiumLinkJackpot,
  'High RTP': StaticJackpot,

  // Legacy mappings for compatibility
  'BELL_LINK': BellLinkJackpot,
  'BELL_LINK_BOOST': BellLinkBoostJackpot,
  'HIGH_CASH': HighCashJackpot,
  'INSTANT': InstantLinkJackpot,
  'MYSTERY': MysteryJackpot,
  'VIP_BELL_LINK': VipBellLinkJackpot,
  'VIP_BELL_LINK_BOOST': VipBellLinkBoostJackpot,
  'VIP_HIGH_CASH': HighCashJackpot,
  'PROGRESSIVE': StaticJackpot,
  'GODS_AND_KINGS': GodsAndKingsJackpot,
  'VIP_GODS_AND_KINGS': VipGodsAndKingsJackpot,
  'PREMIUM_LINK': PremiumLinkJackpot,
  'HIGH_RTP': StaticJackpot,
};

const JackpotPanel: FC<JackpotPanelProps> = memo(({
  jackpotName,
  sectionName,
  games = []
}) => {
  // JackpotPanel component

  const getAnimatedValue = useJackpotsStore(state => state.getAnimatedValue);
  const storeCurrency = useCurrency();

  // The rows that open the drawer: the tiers this family keeps statistics for (High Cash's
  // MINOR / MINI have none, so they stay inert; a listed level nobody has won yet opens the
  // drawer's empty state). Derived from the name alone and memoised, so the `StandardJackpot`
  // memo (which writes its odometer straight to the DOM) neither gains a subscription nor sees a
  // fresh array every render.
  const statisticsLevelKeys = useMemo(() => getJackpotStatisticsLevelKeys(jackpotName), [jackpotName]);
 
  // No need for jackpot data anymore - we get animated values directly
  // const jackpotData = useMemo(() => {
  //   const data = jackpots.get(jackpotName);
  //   return data;
  // }, [jackpots, jackpotName]);

  // Get the appropriate component for this jackpot type
  const JackpotComponent = useMemo(() => {
    if (!sectionName) return null;

    return componentJackpotMap[sectionName] || StandardJackpot;
  }, [sectionName]);

  // Extract jackpot values from the animated store
  // Calculate denomination from games in this section (matching old lobby behavior)
  const currency = storeCurrency || EGT;
  const denomination = useMemo(() => {
    if (games && games.length > 0) {
      // Use the same logic as old lobby - get min denomination from games in section
      return getGamesMinDenomination(games, currency);
    }
    // Fallback to default denomination
    return DEFAULT_DENOMINATION;
  }, [games, currency]);

  if (!JackpotComponent) {
    return null;
  }

  // Get animated values directly from the simple store
  // Remove memoization so values update reactively when store changes
  const values = {
    grand: getAnimatedValue(jackpotName, 1), // Level 1 = GRAND
    major: getAnimatedValue(jackpotName, 2), // Level 2 = MAJOR
    minor: getAnimatedValue(jackpotName, 3), // Level 3 = MINOR
    mini: getAnimatedValue(jackpotName, 4),  // Level 4 = MINI
  };

  // PERFORMANCE: Debug logging removed for production

  // Debug: Add visible content to verify rendering
  return (
    <div className={styles.jackpotPanel} data-testid={testIds.jackpots.panel(sectionName)}>
      <JackpotComponent
        icons={{
          panel: '',  // Will be set by specific components
          grand: '',
          major: '',
          minor: '',
          mini: '',
        }}
        values={values}
        currency={currency}
        denomination={denomination} // Default denomination
        jackpotName={jackpotName}
        sectionName={sectionName}
        statisticsLevelKeys={statisticsLevelKeys}
      />
    </div>
  );
});

JackpotPanel.displayName = 'JackpotPanel';

export default JackpotPanel;
